import { useDropzone } from "react-dropzone";
import { head } from "ramda";
import { useCallback } from "react";
import * as Sentry from "@sentry/react";

/**
 * Reports an upload failure to the caller and to Sentry.
 *
 * `fetch` REJECTS on a CORS or network failure — it does not come back with a
 * non-200 response — so a bare `await fetch(...)` followed by a status check
 * misses that entire class of failure. The rejection then escaped `onUpload`
 * (which callers invoke without awaiting), becoming an unhandled promise
 * rejection: `onError` never ran, nothing reached the technician, and the
 * "Confirm & Upload" button simply stayed disabled forever with no explanation.
 */
const reportUploadFailure = (onError: any, error: unknown, context: Record<string, any>) => {
  Sentry.captureException(error instanceof Error ? error : new Error(String(error)), { extra: context });
  onError(error);
};

type UseS3UploadInput = {
  contactId: string;
  onUploadStart: (img: any) => Promise<string>;
  onUploadDone: ({ url, fileName }: { url: string; fileName: string }) => void;
  onError: any;
  myFiles;
  setMyFiles;
};

export function useS3Upload({
  onUploadStart,
  onUploadDone,
  onError,
  contactId,
  myFiles,
  setMyFiles,
}: UseS3UploadInput) {
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const fileToUpload = head<any>(acceptedFiles);
      try {
        const presignedUploadUrl = await onUploadStart(fileToUpload.name);
        if (setMyFiles) {
          setMyFiles(acceptedFiles);
        }
        // Upload the image to our pre-signed URL.
        const response = await fetch(
          new Request(presignedUploadUrl, {
            method: "PUT",
            body: fileToUpload,
            headers: new Headers({
              "Content-Type": "*",
            }),
          })
        );
        if (response.status !== 200) {
          reportUploadFailure(onError, new Error(`S3 responded ${response.status}`), {
            presignedUploadUrl,
            status: response.status,
          });
          return;
        }
        const url = `${process.env.REACT_APP_S3_MEDIA_URL}/contacts/${contactId}/${encodeURI(fileToUpload.name)}`;
        await onUploadDone({ fileName: fileToUpload.name, url });
        // Let the caller know that the upload is done, so that it can send the URL
        // to the backend again, persisting it to the database.
      } catch (error) {
        reportUploadFailure(onError, error, { fileName: fileToUpload?.name });
      }
    },
    [contactId, onError, onUploadDone, onUploadStart, setMyFiles]
  );

  return useDropzone({
    onDrop: onDrop,
    multiple: false,
  });
}

export function useSimpleS3Upload({
  onUploadStart,
  onUploadDone,
  onError,
  contactId,
  myFiles,
  setMyFiles,
}: UseS3UploadInput) {
  const onUpload = useCallback(
    async (acceptedFiles) => {
      const fileToUpload = head<any>(acceptedFiles);
      try {
        const presignedUploadUrl = await onUploadStart(fileToUpload.name);
        if (setMyFiles) {
          setMyFiles(acceptedFiles);
        }
        // Upload the image to our pre-signed URL.
        const response = await fetch(
          new Request(presignedUploadUrl, {
            method: "PUT",
            body: fileToUpload,
            headers: new Headers({
              "Content-Type": "*",
            }),
          })
        );
        if (response.status !== 200) {
          reportUploadFailure(onError, new Error(`S3 responded ${response.status}`), {
            presignedUploadUrl,
            status: response.status,
          });
          return;
        }
        const url = `${process.env.REACT_APP_S3_MEDIA_URL}/contacts/${contactId}/${encodeURI(fileToUpload.name)}`;
        await onUploadDone({ fileName: fileToUpload.name, url });
        // Let the caller know that the upload is done, so that it can send the URL
        // to the backend again, persisting it to the database.
      } catch (error) {
        reportUploadFailure(onError, error, { fileName: fileToUpload?.name });
      }
    },
    [contactId, onError, onUploadDone, onUploadStart, setMyFiles]
  );

  return {
    onUpload: onUpload,
  };
}
